import React, { useState, useRef, useEffect } from 'react';
import Button from '../../components/ui/Button';
import InputField from '../../components/ui/InputField';
import Dialog from '../../components/ui/Dialog';
import Card from '../../components/ui/Card';
import TrashIcon from '../../components/icons/TrashIcon';
import RedoIcon from '../../components/icons/RedoIcon';
import PlusIcon from '../../components/icons/PlusIcon';

interface DrawHistory {
  id: string;
  winner: string;
  timestamp: Date;
}

interface Participant {
  name: string;
  probability: number;
}

const ROULETTE_COLORS = ['#FF6F0F', '#4CAF50', '#2196F3', '#FFC107', '#9C27B0', '#f44336'];

const RouletteDrawer: React.FC = () => {
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [currentName, setCurrentName] = useState<string>('');
  const [isDrawing, setIsDrawing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [drawHistory, setDrawHistory] = useState<DrawHistory[]>([]);
  const [openHistory, setOpenHistory] = useState<boolean>(false);
  const [currentDisplayName, setCurrentDisplayName] = useState<string>('');
  const [winner, setWinner] = useState<string>('');
  const [rotation, setRotation] = useState<number>(0);
  const [selectedParticipant, setSelectedParticipant] = useState<Participant | null>(null);
  const [openProbabilityDialog, setOpenProbabilityDialog] = useState<boolean>(false);
  const animationRef = useRef<number | null>(null);

  const addName = () => {
    if (!currentName.trim()) {
      setError('이름을 입력해주세요.');
      return;
    }
    if (participants.some(p => p.name === currentName.trim())) {
      setError('이미 추가된 이름입니다.');
      return;
    }
    setParticipants(prev => {
      const newParticipants = [...prev, { name: currentName.trim(), probability: 100 / (prev.length + 1) }];
      setRotation(0);
      return newParticipants;
    });
    setCurrentName('');
    setError(null);
  };

  const removeName = (index: number) => {
    setParticipants(prev => {
      const newParticipants = prev.filter((_, i) => i !== index);
      setRotation(0);
      return newParticipants;
    });
  };

  const validateInputs = () => {
    if (participants.length < 2) {
      setError('최소 2명 이상의 이름을 추가해주세요.');
      return false;
    }
    return true;
  };

  const drawWinner = () => {
    if (!validateInputs()) return;
    setIsDrawing(true);
    setWinner('');
    setRotation(0);
    const maxAnimationCount = 50;
    let animationCount = 0;
    const totalProbability = participants.reduce((sum, p) => sum + p.probability, 0);
    const weights = participants.map(p => p.probability / totalProbability);
    const random = Math.random();
    let cumulativeWeight = 0;
    let selectedIndex = 0;
    for (let i = 0; i < weights.length; i++) {
      cumulativeWeight += weights[i];
      if (random <= cumulativeWeight) {
        selectedIndex = i;
        break;
      }
    }
    const selectedWinner = participants[selectedIndex].name;

    const animate = () => {
      if (animationCount >= maxAnimationCount) {
        setWinner(selectedWinner);
        setIsDrawing(false);
        setDrawHistory(prev => [{ id: Date.now().toString(), winner: selectedWinner, timestamp: new Date() }, ...prev]);
        return;
      }
      const randomName = participants[Math.floor(Math.random() * participants.length)].name;
      setCurrentDisplayName(randomName);
      setRotation(prev => prev + 30);
      animationCount++;
      animationRef.current = requestAnimationFrame(animate);
    };
    animationRef.current = requestAnimationFrame(animate);
  };

  const handleProbabilityChange = (index: number, value: number) => {
    const newValue = Math.min(100, Math.max(0, value));
    const currentTotal = participants.reduce((sum, p) => sum + p.probability, 0);
    const oldValue = participants[index].probability;
    const newTotal = currentTotal - oldValue + newValue;
    if (newTotal > 100) {
      setError(`확률의 합이 100%를 초과할 수 없습니다. (현재: ${currentTotal}%, 변경 후: ${newTotal}%)`);
      return;
    }
    setError(null);
    setParticipants(prev => {
      const newParticipants = [...prev];
      newParticipants[index] = { ...newParticipants[index], probability: newValue };
      setRotation(0);
      return newParticipants;
    });
  };

  useEffect(() => {
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, []);

  const totalProbability = participants.reduce((sum, p) => sum + p.probability, 0);
  const conicGradient = participants.length > 0
    ? `conic-gradient(${participants.map((p, i) => {
        const start = participants.slice(0, i).reduce((s, x) => s + (x.probability / 100) * 360, 0);
        const end = start + (p.probability / 100) * 360;
        return `${ROULETTE_COLORS[i % ROULETTE_COLORS.length]} ${start}deg ${end}deg`;
      }).join(', ')}${totalProbability < 100 ? `, white ${(totalProbability / 100) * 360}deg 360deg` : ''})`
    : '';

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: 24 }}>
      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
        <div style={{ flex: '0 0 280px' }}>
          <p style={{ marginBottom: 8 }}>참가자 추가</p>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <InputField
              value={currentName}
              onChange={setCurrentName}
              placeholder="이름"
            />
            <Button variant="primary" onClick={addName} leftIcon={<PlusIcon width={18} height={18} />}>추가</Button>
          </div>
          {error && (
            <div style={{ marginBottom: 16, padding: 12, background: '#ffebee', borderRadius: 8, color: '#c62828', fontSize: 14 }}>{error}</div>
          )}
          <div style={{ maxHeight: 'calc(100vh - 320px)', overflowY: 'auto', padding: 8, background: 'var(--Color-background-normal)', borderRadius: 8, minHeight: 50 }}>
            {participants.map((participant, index) => (
              <div
                key={index}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 8, marginBottom: 4, background: '#fff', borderRadius: 4 }}
              >
                <span style={{ minWidth: 80 }}>{participant.name}</span>
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={participant.probability}
                  onChange={(e) => handleProbabilityChange(index, Math.min(100, Math.max(0, Number(e.target.value))))}
                  style={{ width: 64, padding: 4 }}
                />
                <span style={{ fontSize: 12, color: '#666' }}>%</span>
                <button type="button" onClick={() => removeName(index)} style={{ padding: 4, background: 'none', border: 'none', cursor: 'pointer' }}>
                  <TrashIcon width={18} height={18} />
                </button>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 24, display: 'flex', gap: 8 }}>
            <Button variant="primary" onClick={drawWinner} disabled={isDrawing} leftIcon={<RedoIcon width={18} height={18} />} style={{ flex: 1 }}>
              {isDrawing ? '추첨 중...' : '룰렛 돌리기'}
            </Button>
            <button
              type="button"
              onClick={() => setOpenHistory(true)}
              style={{ padding: 8, background: 'var(--Color-fill-brand-color)', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer' }}
              title="추첨 기록"
            >
              📋
            </button>
          </div>
          <Card padding="16px" style={{ marginTop: 16 }}>
            <p style={{ margin: 0, fontSize: 14 }}>확률 합계: {totalProbability}%</p>
          </Card>
        </div>

        <div style={{ flex: '1 1 400px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div
            style={{
              width: '100%',
              maxWidth: 400,
              aspectRatio: '1',
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'var(--Color-background-normal)',
              borderRadius: '50%',
              padding: 24,
              border: '2px solid var(--Color-fill-brand-color)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                borderRadius: '50%',
                transform: `rotate(${rotation}deg)`,
                transition: 'transform 0.1s linear',
                pointerEvents: 'none',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  borderRadius: '50%',
                  background: conicGradient || 'transparent',
                  opacity: 0.3,
                }}
              />
              {participants.length > 0 &&
                participants.map((participant, index) => {
                  const startAngle = participants.slice(0, index).reduce((s, p) => s + (p.probability / 100) * 360, 0);
                  const endAngle = startAngle + (participant.probability / 100) * 360;
                  const centerAngle = (startAngle + endAngle) / 2;
                  return (
                    <div
                      key={index}
                      style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: `rotate(${centerAngle}deg) translate(0, -60%) rotate(${-centerAngle}deg)`,
                        transformOrigin: 'center center',
                        textAlign: 'center',
                        color: 'var(--Color-fill-brand-color)',
                        fontWeight: 700,
                        fontSize: '1rem',
                      }}
                    >
                      <div>{participant.name}</div>
                      <div style={{ fontSize: 12, opacity: 0.8 }}>{participant.probability}%</div>
                    </div>
                  );
                })}
            </div>
            {participants.length === 0 && (
              <p style={{ color: '#666', margin: 0 }}>참가자를 추가해주세요</p>
            )}
          </div>
          {(isDrawing || winner) && (
            <div style={{ width: '100%', maxWidth: 400, textAlign: 'center', background: 'rgba(255,255,255,0.95)', borderRadius: 16, padding: 24, marginTop: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              <p style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--Color-fill-brand-color)' }}>
                {isDrawing ? currentDisplayName : winner}
              </p>
            </div>
          )}
        </div>
      </div>

      <Dialog open={openProbabilityDialog} onClose={() => setOpenProbabilityDialog(false)} title="확률 설정" actions={
        <Button variant="primary" onClick={() => setOpenProbabilityDialog(false)}>확인</Button>
      }>
        {selectedParticipant && (
          <div style={{ padding: 16 }}>
            <p style={{ marginBottom: 8 }}>{selectedParticipant.name}의 확률 (%)</p>
            <input
              type="range"
              min={0}
              max={100}
              value={selectedParticipant.probability}
              onChange={(e) => {
                const idx = participants.findIndex(p => p.name === selectedParticipant.name);
                if (idx !== -1) handleProbabilityChange(idx, Number(e.target.value));
              }}
              style={{ width: '100%' }}
            />
            <span>{selectedParticipant.probability}%</span>
          </div>
        )}
      </Dialog>

      <Dialog open={openHistory} onClose={() => setOpenHistory(false)} title="추첨 기록" actions={
        <Button variant="primary" onClick={() => setOpenHistory(false)}>닫기</Button>
      }>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {drawHistory.map((draw) => (
            <li key={draw.id} style={{ padding: '12px 0', borderBottom: '1px solid #eee' }}>
              <strong>{draw.winner}</strong>
              <span style={{ marginLeft: 8, fontSize: 12, color: '#666' }}>{draw.timestamp.toLocaleString()}</span>
            </li>
          ))}
        </ul>
      </Dialog>
    </div>
  );
};

export default RouletteDrawer;
